import React, { useState } from 'react';

// Map of technology names to their Devicon paths
const TECH_ICONS: Record<string, { path: string; color?: string }> = {
  react: { path: 'react/react-original', color: '#61DAFB' },
  typescript: { path: 'typescript/typescript-original', color: '#3178C6' },
  javascript: { path: 'javascript/javascript-original', color: '#F7DF1E' },
  python: { path: 'python/python-original', color: '#3776AB' },
  java: { path: 'java/java-original', color: '#ED8B00' },
  nodejs: { path: 'nodejs/nodejs-original', color: '#339933' },
  postgresql: { path: 'postgresql/postgresql-original', color: '#4169E1' },
  docker: { path: 'docker/docker-original', color: '#2496ED' },
  git: { path: 'git/git-original', color: '#F05032' },
  linux: { path: 'linux/linux-original', color: '#FCC624' },
  aws: { path: 'amazonwebservices/amazonwebservices-plain-wordmark', color: '#FF9900' },
  mysql: { path: 'mysql/mysql-original', color: '#4479A1' },
  bash: { path: 'bash/bash-original', color: '#4EAA25' },
  vscode: { path: 'vscode/vscode-original', color: '#007ACC' },
  github: { path: 'github/github-original', color: '#181717' },
  jest: { path: 'jest/jest-plain', color: '#C21325' },
  tailwindcss: { path: 'tailwindcss/tailwindcss-original', color: '#06B6D4' },
};

interface TechLogoProps {
  name: string;
  size?: number;
  className?: string;
  showLabel?: boolean;
}

export default function TechLogo({
  name,
  size = 32,
  className = '',
  showLabel = false,
}: TechLogoProps) {
  const [hasError, setHasError] = useState(false);
  const normalizedName = name.toLowerCase().replace(/[.\s]/g, '');
  const tech = TECH_ICONS[normalizedName];

  if (!tech || hasError) {
    // Fallback: display initials
    return (
      <div
        className={`flex items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-bold ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.4 }}
        title={name}
      >
        {name.slice(0, 2).toUpperCase()}
      </div>
    );
  }

  const iconUrl = `https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/${tech.path}.svg`;

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <img
        src={iconUrl}
        alt={`${name} logo`}
        width={size}
        height={size}
        className="object-contain"
        onError={() => setHasError(true)}
        loading="lazy"
      />
      {showLabel && (
        <span className="text-xs text-slate-600 font-medium capitalize">
          {name}
        </span>
      )}
    </div>
  );
}

// Export the list of available technologies
export const AVAILABLE_TECHNOLOGIES = Object.keys(TECH_ICONS);
